import { Form } from "antd"
import { getSalesInitialValues } from "./salesInitialValues"
import { memo, useCallback, useEffect, useRef } from "react"
import { Alert, Group, Legend, SelectInput } from "components/common"
import getSalesValidationSchema from "./addSalesValidationSchema"
import { useSelector } from "react-redux"
import { schemaRules } from "features/shared/utils"
import SaleTypeBookingOptions from "./components/saleTypeBookingOptions "
import NextTaskSelector from "./components/nextTaskSelector"
import ClientDetails from "./components/clientDetails"
import ClientProfileForm from "features/tasks/components/clientProfileForm/clientProfileForm"
import { ACTION_TYPES } from "utils/gtm/gtmEventsData"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import { debounce } from "lodash"
import { t } from "i18next"
import PaymentSection from "./components/paymentSection/paymentSection"
import { useAddSalesContext } from "../../addSalesContext/addSalesContext"
import ProjectDetails from "./components/projectDetails/projectDetails"
import ClientIbanForm from "features/shared/components/clientIbanForm/clientIbanForm"

const AddSalesForm = ({ formRef, from }) => {
  const userPlateformKey = useSelector(state => state.app.user?.info?.AgencyConfiguration?.Platform?.platformKey)

  const {
    form,
    stateManager,
    addSalesApis,
    formWatchers,
    isEscrowSelfFinance,
    billingType,
    client,
    lead,
    lastPaymentStatus,
    optionalSelections
  } = useAddSalesContext()
  const { project, unit, amount, bookingPlatform } = formWatchers
  const {
    modeOfPayments,
    leadSalesInfo,
    activeBookingPlatforms,
    addSales,
    reset,
    fetchClientAnswers,
    isFetchingLeadDetails,
    isLoading,
    promoTags,
    promoTagsLoading,
    getClientAnswers,
    banks,
    clientIban,
    getClientIban,
  } = addSalesApis

  const clientProfileRef = useRef()
  const clientIbanRef = useRef()
  const rules = schemaRules(getSalesValidationSchema(userPlateformKey))

  const checkAbovePDP = () => {
    if (leadSalesInfo?.current >= 0 && +amount > 0) {
      return +amount >= leadSalesInfo?.remainParDowPayment ? true : false
    } else if (amount > 0) {
      return +amount >= stateManager?.dealPrice * project?.pdpPercentage ? true : false
    }
    return false
  }

  const getFilePayload = file => {
    return {
      fileName: file?.name || file?.response?.file?.name || "file",
      s3FileKey: file?.response?.key || file?.s3FileKey,
    }
  }

  const onSubmit = async eventPayload => {
    const val = form.getFieldsValue()
    let bookingForm =
      (leadSalesInfo?.current >= 0 ? leadSalesInfo?.projectBookingForm : project?.needsBookingForm) && checkAbovePDP()
    await addSales({
      lead,
      leadSalesInfo,
      bookingForm,
      banks,
      modeOfPayments,
      stateManager,
      optionalSelections,
      ...val,
      ...(bookingPlatform &&
        bookingPlatform !== -1 && {
          bookingPlatform: activeBookingPlatforms?.find(e => e?.id === bookingPlatform)?.value,
          ...(val?.bookingPlatformAttachment?.length && {
            bookingPlatformAttachment: getFilePayload(val?.bookingPlatformAttachment?.[0]),
          }),
        }),
    })
      .unwrap()
      .then(() => {
        formRef?.current?.setVisible(false)
        fireEvent({
          ...ACTION_TYPES.lead_tab_submit_sale,
          ...from,
          response: "200",
          client_id: lead?.Client?.id || client?.id,
          ...eventPayload,
          unit_id: eventPayload?.unit_id || leadSalesInfo?.unitId || val?.unit?.id || stateManager?.unit?.listingId,
          unit_discount: stateManager?.discountAmount,
          project_name: val?.project?.name || leadSalesInfo?.projectName || stateManager?.project?.name,
        })
        form.resetFields()
        isEscrowSelfFinance && !clientIban?.data?.iban && clientIbanRef?.current?.setVisible(true)
      })
      .catch(e => {
        fireEvent({
          ...ACTION_TYPES.lead_tab_submit_sale,
          ...from,
          response: e?.status,
          client_id: lead?.Client?.id || client?.id,
          ...eventPayload,
          unit_id: eventPayload?.unit_id || leadSalesInfo?.unitId || val?.unit?.id,
          unit_discount: stateManager?.discountAmount,
          project_name: val?.project?.name || leadSalesInfo?.projectName,
          dealPrice: stateManager?.dealPrice,
        })
      })
  }
  const debounceOnSubmit = useCallback(
    debounce((callBack = () => {}) => {
      callBack()
    }, 500),
    []
  )

  useEffect(() => {
    if (isEscrowSelfFinance) getClientIban({ clientId: leadSalesInfo?.inquiry?.Client?.id }, true)
  }, [billingType, leadSalesInfo])

  const onFinish = e => {
    const eventPayload = {
      client_name: client?.name,
      project_name: e.project?.name,
      comments: e.comment,
      unit_id: e.unit?.id,
      amount: e.amount,
      payment_mode: modeOfPayments?.find(item => item?.key === e?.modeOfPayment)?.name,
      receipt_number: e.receiptNumber,
      lead_id: lead.id,
    }
    let ApiCall = () => {
      getClientAnswers(client?.id)
        .unwrap()
        .then(e => {
          if (!e?.profileFilled) {
            clientProfileRef?.current.setVisible(true)
          } else {
            reset()
            onSubmit(eventPayload)
          }
        })
    }
    debounceOnSubmit(ApiCall)
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        name="add-sales-form"
        initialValues={getSalesInitialValues(client)}
        onFinish={onFinish}
        scrollToFirstError
        disabled={fetchClientAnswers || isFetchingLeadDetails || isLoading}
        preserve={false}
      >
        <Group template="initial" gap="24px">
          <ProjectDetails lead={lead} rules={rules} />
          {modeOfPayments && !modeOfPayments?.length ? (
            <Alert
              type="error"
              message={t("No acceptable payment methods are available in the project configurations")}
            />
          ) : (
            <>
              {leadSalesInfo?.isFirstSalesTask && project && unit && !!promoTags?.length ? (
                <div>
                  <Legend title={t("Project Tags")} />
                  <Form.Item name={"promoTagIds"}>
                    <SelectInput
                      placeholder={t("Select Project Promo Tags")}
                      options={promoTags}
                      loading={promoTagsLoading}
                      mode="multiple"
                      getOptionValue={option => option?.id}
                      allowClear
                    />
                  </Form.Item>
                </div>
              ) : null}
              {unit && project && <SaleTypeBookingOptions rules={rules} />}
              <PaymentSection rules={rules} clientIbanRef={clientIbanRef} />
              <ClientDetails rules={rules} />
              <NextTaskSelector rules={rules} />
            </>
          )}
        </Group>
      </Form>
      <ClientProfileForm formRef={clientProfileRef} FormRef={formRef} onSuccess={onSubmit} client={client} />
      <ClientIbanForm
        ref={clientIbanRef}
        clientId={client?.id}
        clientIban={clientIban?.data?.iban}
        paymentCompleted={lastPaymentStatus === "completed"}
        contractId={leadSalesInfo?.contracts?.[0]?.contractId}
      />
    </>
  )
}
export default memo(AddSalesForm)
